/* ═══════════════════════════════════════════════════════════════
   FAQ search, filtering and disclosure

   Everything is rendered server-side; this only hides and shows.
   Nothing is rebuilt from a template, so the answers stay in the
   markup where search engines and a reader with JavaScript off can
   both still reach them - the panels are open by default in the
   HTML and closed here on init.

   Search matches the question and the answer text, so "damaged"
   finds the entry about arriving damaged even though that word is
   only in the body.
   ═══════════════════════════════════════════════════════════════ */

(function () {
  'use strict';

  function normalise(value) {
    return (value || '').toLowerCase().replace(/\s+/g, ' ').trim();
  }

  /* Search terms are user input and the question is re-rendered as
     HTML to mark them, so both are escaped first. */
  function escapeHtml(value) {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  function escapeRegex(value) {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  function Faq(root) {
    var input = root.querySelector('[data-mcq-search]');
    var clear = root.querySelector('[data-mcq-clear]');
    var filters = Array.prototype.slice.call(root.querySelectorAll('[data-mcq-filter]'));
    var items = Array.prototype.slice.call(root.querySelectorAll('[data-mcq-item]'));
    var groups = Array.prototype.slice.call(root.querySelectorAll('[data-mcq-group]'));
    var count = root.querySelector('[data-mcq-count]');
    var empty = root.querySelector('[data-mcq-empty]');

    var category = 'all';

    /* Read once: the answer text never changes, and re-reading it
       per keystroke across 30-odd entries is wasted work. */
    items.forEach(function (item) {
      var q = item.querySelector('[data-mcq-question]');
      var a = item.querySelector('[data-mcq-answer]');
      item.__q = q ? q.textContent : '';
      item.__haystack = normalise(item.__q + ' ' + (a ? a.textContent : ''));
      item.__qEl = q;
    });

    function setOpen(item, open) {
      var button = item.querySelector('[data-mcq-toggle]');
      var panel = item.querySelector('[data-mcq-answer]');
      if (!button || !panel) return;
      button.setAttribute('aria-expanded', open ? 'true' : 'false');
      panel.hidden = !open;
    }

    function highlight(item, term) {
      if (!item.__qEl) return;

      if (!term) {
        item.__qEl.textContent = item.__q;
        return;
      }

      var re = new RegExp('(' + escapeRegex(term) + ')', 'ig');
      item.__qEl.innerHTML = escapeHtml(item.__q).replace(re, '<mark>$1</mark>');
    }

    function apply() {
      var term = normalise(input ? input.value : '');
      var shown = 0;

      items.forEach(function (item) {
        var inCategory = category === 'all' || item.getAttribute('data-category') === category;
        var matches = !term || item.__haystack.indexOf(term) !== -1;
        var visible = inCategory && matches;

        item.hidden = !visible;
        if (visible) shown++;

        highlight(item, term);

        /* A searched-for answer is worth opening straight away;
           browsing a category is not, or the page becomes a wall. */
        if (visible && term) {
          setOpen(item, true);
        } else if (!visible) {
          setOpen(item, false);
        }
      });

      /* A group heading is only meaningful when the whole list is
         on show, and then only if it still has visible entries. */
      groups.forEach(function (group) {
        var browsing = !term && category === 'all';
        var has = false;
        if (browsing) {
          has = items.some(function (item) {
            return !item.hidden && item.getAttribute('data-category') === group.getAttribute('data-mcq-group');
          });
        }
        group.hidden = !browsing || !has;
      });

      if (count) {
        if (term || category !== 'all') {
          count.textContent =
            shown === 0
              ? ''
              : shown + (shown === 1 ? ' answer' : ' answers') + (term ? ' for “' + input.value.trim() + '”' : '');
        } else {
          count.textContent = '';
        }
      }

      if (empty) empty.hidden = shown !== 0;
      if (clear) clear.hidden = !(input && input.value);
    }

    /* ── events ─────────────────────────────────────────── */

    root.addEventListener('click', function (event) {
      var toggle = event.target.closest('[data-mcq-toggle]');
      if (toggle) {
        var item = toggle.closest('[data-mcq-item]');
        var open = toggle.getAttribute('aria-expanded') === 'true';
        setOpen(item, !open);
        return;
      }

      var filter = event.target.closest('[data-mcq-filter]');
      if (filter) {
        category = filter.getAttribute('data-mcq-filter');
        filters.forEach(function (f) {
          f.setAttribute('aria-pressed', f === filter ? 'true' : 'false');
        });
        apply();
      }
    });

    if (input) {
      input.addEventListener('input', apply);
      /* Enter would otherwise submit whatever form wraps the page. */
      input.addEventListener('keydown', function (event) {
        if (event.key === 'Enter') event.preventDefault();
      });
    }

    if (clear) {
      clear.addEventListener('click', function () {
        input.value = '';
        input.focus();
        apply();
      });
    }

    /* Close everything now that the script is running. They are
       open in the markup so the content is readable without JS. */
    items.forEach(function (item) {
      setOpen(item, false);
    });

    /* A link to #some-question opens that entry and scrolls to it. */
    function openFromHash() {
      var hash = window.location.hash.replace('#', '');
      if (!hash) return;
      var item = root.querySelector('[data-mcq-item][id="' + CSS.escape(hash) + '"]');
      if (!item) return;
      item.hidden = false;
      setOpen(item, true);
      item.scrollIntoView({ block: 'center' });
    }

    window.addEventListener('hashchange', openFromHash);

    apply();
    openFromHash();
  }

  function init() {
    document.querySelectorAll('[data-mcq]').forEach(function (root) {
      if (root.__mcqReady) return;
      root.__mcqReady = true;
      Faq(root);
    });
  }

  document.addEventListener('shopify:section:load', init);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
